import { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { Button } from '@tcl-boron-prefabs/button';
import typography from '@tcl-boron-styles/typography/dist/index.module.css';
import { featureEnabledInEnvironment } from '../../../../config/feature-toggles';
import {
  allNeuroConsentV2Fields,
  BillingType,
  IS_LEGALLY_AUTHORIZED_REPRESENTATIVE_OPTIONS_V2,
  IsLegallyAuthorizedRepresentativeOption,
  NEURO_CONSENT_TO_TEST_V2_KEY,
  NEURO_CONSENT_TO_TEST_V2_TITLE,
  NeuroConsentV2ContentPart1,
  NeuroConsentV2ContentPart2,
  NeuroConsentV2ContentPart3,
  NeuroConsentV2ContentPart4,
  OTHER_OPTION,
} from '@tempus/patient-forms-service-shared';
import { FinancialAssistanceStatuses, InsuranceType } from 'requisition-service-shared';
import { Typography } from '@mui/material';
import { Form } from 'react-final-form';
import { createUseStyles } from 'react-jss';
import usePortalContext from '../../context/usePortalContext';
import withPortalContainer from '../../hooks/withPortalContainer';
import { PatientInfo } from '../patient-info/shared';
import { NotificationType, useNotificationContext } from '../../notifications/NotificationContext';
import logger from '../../../../utils/logger';
import { NeuroConsentV2StartFormResponse } from '../../../types/NeuroConsentV2';
import ScreenHeader from '../../components/ScreenHeader';
import LoadingIndicator from '../../components/LoadingIndicator';
import { required } from '../../shared/form/validators';
import FormSingleSelectDropdown from '../../shared/form/FormSingleSelectDropdown';
import FormTextInput from '../../shared/form/FormTextInput';
import FormSingleDatePicker from '../../shared/form/FormSingleDatePicker';
import CommercialInsurancePaymentPlanSubform from './components/CommercialInsurancePaymentPlanSubform';
import SelfPayPaymentPlanSubform from './components/SelfPayPaymentPlanSubform';
import { InsuranceInformationSubform } from './components/InsuranceInformationSubform';
import Condition from '../../shared/form/ConditionalField';
import FormSignature from '../../shared/form/FormSignature';
import { findFieldConfig } from '../../utils/findFieldLabel';
import ErrorScreen from '../../components/ErrorScreen';
import { Route, Switch, useHistory } from 'react-router-dom';
import { FinancialAssistanceV2 } from '../financial-assistance/v2/FinancialAssistanceV2';
import { FinancialAssistance as FinancialAssistanceV1 } from '../financial-assistance/v1/FinancialAssistance';
import classNames from 'classnames';
import { Fail } from '@tcl-boron-icons/icons';
import { ErrorPalette, GrayscalePalette } from '@tcl-boron-colors/colors';
import { HttpError } from '../../../utils';
import { SubmissionError, useErrorStyles } from '../../components/SubmissionError';

const PAYMENT_DECLINED_STATUS = 402;

const useStyles = createUseStyles({
  container: {
    flexDirection: 'column',
    flexGrow: 1,
    gap: '16px',
  },
  button: {
    maxWidth: '200px',
    flexGrow: 1,
  },
  inputContainer: {
    padding: '16px',
    border: `1px solid ${GrayscalePalette[400]}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  contentContainer: {
    '& ul': {
      paddingLeft: '16px',
    },
  },
});

const PaymentDeclinedError: React.FC<{ onRetry: () => void }> = ({ onRetry }) => {
  const classes = useErrorStyles();

  return (
    <div className={classes.errorContainer} data-testid="payment-declined-error">
      <div className={classes.errorHeader}>
        <Fail color={ErrorPalette[700]} />
        <p className={classes.errorText} style={{ margin: 0 }}>
          <strong>Payment Declined</strong>
        </p>
      </div>
      <p className={classes.errorText}>
        Your card was declined. Please try a different payment method. If this issue persists, please contact the Tempus
        Customer Success team at <a href="mailto:help@tempus.com">help@tempus.com</a> or call{' '}
        <a href="tel:312-598-9961">312-598-9961</a>.
      </p>
      <Button ariaLabel="Retry" onClick={onRetry} data-testid="retry-button">
        Retry
      </Button>
    </div>
  );
};

const ConsentToTest: React.FC<{ nextAction?: () => Promise<void> }> = ({ nextAction }) => {
  const [submitting, setSubmitting] = useState(false);
  const [isStripeComplete, setIsStripeComplete] = useState(false);
  const [startFormProps, setStartFormProps] = useState<NeuroConsentV2StartFormResponse & PatientInfo>();
  const [loading, setLoading] = useState(true);
  const [submissionError, setSubmissionError] = useState(false);
  const [cardDeclinedError, setCardDeclinedError] = useState(false);
  const { fetchHelper, patient } = usePortalContext();
  const { notify } = useNotificationContext();
  const history = useHistory();
  const classes = useStyles();

  const startOrSkipForm = async () => {
    setLoading(true);
    await fetchHelper<PatientInfo>(`/patient-info`)
      .then(async (patientInfo) => {
        if (patientInfo.patientConsentReceived) {
          // skip consent if already received
          if (nextAction) {
            nextAction();
          } else {
            throw new Error('Next action is not defined - is the component being used outside of onboarding?');
          }
        }
        const startFormProps = await fetchHelper<NeuroConsentV2StartFormResponse>(`/form/start/${patient.orderId}`, {
          method: 'POST',
          body: {
            formType: NEURO_CONSENT_TO_TEST_V2_KEY,
            patientId: patient.patientId,
          },
        });

        setStartFormProps({ ...startFormProps, ...patientInfo });
      })
      .catch((e: any) => {
        logger.error(e, 'Error starting consent form');
        notify(NotificationType.ERROR, 'Error starting consent form');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    startOrSkipForm();
  }, []);

  if (loading) {
    return <LoadingIndicator />;
  }

  if (!startFormProps) {
    return <ErrorScreen />;
  }

  const {
    requireBillTypeSelection,
    faStatus,
    collectGovernmentInsurance,
    stripeCheckoutToken,
    name: { first, last },
    dateOfBirth,
    stripeCustomerId,
  } = startFormProps;

  const submitForm = async (values: any) => {
    setSubmitting(true);
    try {
      if (values.isLegallyAuthorizedRepresentative === IsLegallyAuthorizedRepresentativeOption.YES) {
        delete values.signature;
      } else if (values.isLegallyAuthorizedRepresentative === IsLegallyAuthorizedRepresentativeOption.NO) {
        delete values.representativeSignature;
        delete values.legallyAuthorizedRepresentativeFirstName;
        delete values.legallyAuthorizedRepresentativeLastName;
        delete values.legallyAuthorizedRepresentativeRelationshipToPatient;
      }

      await fetchHelper(`/form/submit/${patient.orderId}`, {
        method: 'POST',
        body: {
          formType: NEURO_CONSENT_TO_TEST_V2_KEY,
          patientId: patient.patientId,
          data: {
            stripeCustomerId,
            collectGovernmentInsurance,
            useSelfPayFlow: values.insuranceCompanyName === OTHER_OPTION.value,
            faStatus,
            ...values,
          },
        },
      });
      if (nextAction) {
        await nextAction();
      } else {
        throw new Error('Next action is not defined - is the component being used outside of onboarding?');
      }
    } catch (error: any) {
      logger.error(error, 'Error submitting consent to test form');
      if (error instanceof HttpError && error.status === PAYMENT_DECLINED_STATUS) {
        setCardDeclinedError(true);
      } else {
        setSubmissionError(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = () => {
    setSubmissionError(false);
    setCardDeclinedError(false);
    startOrSkipForm();
  };

  if (submissionError) {
    return <SubmissionError onRetry={handleRetry} />;
  }

  if (cardDeclinedError) {
    return <PaymentDeclinedError onRetry={handleRetry} />;
  }

  return (
    <Grid container className={classes.container}>
      <ScreenHeader text={NEURO_CONSENT_TO_TEST_V2_TITLE} />
      <Form
        onSubmit={submitForm}
        initialValues={{
          patientFirstName: first,
          patientLastName: last,
          patientDateOfBirth: dateOfBirth,
        }}
        render={({ handleSubmit, valid, values, form }) => {
          const bypassStripeRequirement = ![BillingType.SELF_PAY, BillingType.COMMERCIAL_INSURANCE].includes(
            values.billingType,
          );
          const { billingType, insuranceCompanyName } = values;
          const useSelfPayFlow = insuranceCompanyName === OTHER_OPTION.value;

          const resetBillingFields = () => {
            // Reset all billing-related fields when billing type changes
            const fieldsToReset = [
              'insuranceCompanyName',
              'insuranceProviderName',
              'policyNumber',
              'groupNumber',
              'policyHolderName',
              'relationshipToPolicyHolder',
              'numberOfInstallments',
              'isLegallyAuthorizedRepresentative',
              'legallyAuthorizedRepresentativeFirstName',
              'legallyAuthorizedRepresentativeLastName',
              'legallyAuthorizedRepresentativeRelationshipToPatient',
              'signature',
              'representativeSignature',
            ];

            fieldsToReset.forEach((fieldName) => {
              form.change(fieldName, undefined);
            });
          };

          const resetInsuranceFields = () => {
            const fieldsToReset = [
              'policyNumber',
              'groupNumber',
              'policyHolderName',
              'relationshipToPolicyHolder',
              'numberOfInstallments',
            ];

            fieldsToReset.forEach((fieldName) => {
              form.change(fieldName, undefined);
            });
          };

          const shouldPay =
            faStatus !== FinancialAssistanceStatuses.ApprovedForFullCoverage &&
            [BillingType.SELF_PAY, BillingType.COMMERCIAL_INSURANCE].includes(billingType);
          const submitText = shouldPay ? 'Submit and Pay' : 'Submit';

          return (
            <>
              <FormTextInput
                label={findFieldConfig('patientFirstName', allNeuroConsentV2Fields).label}
                placeholder="Patient first name"
                name="patientFirstName"
                data-testid="patient-first-name"
                disabled
              />
              <FormTextInput
                label={findFieldConfig('patientLastName', allNeuroConsentV2Fields).label}
                placeholder="Patient last name"
                name="patientLastName"
                data-testid="patient-last-name"
                disabled
              />
              <FormSingleDatePicker
                label={findFieldConfig('patientDateOfBirth', allNeuroConsentV2Fields).label}
                name="patientDateOfBirth"
                data-testid="patient-dob"
                disabled
              />
              <div className={classes.contentContainer}>
                <NeuroConsentV2ContentPart1 />
              </div>
              <a
                className={classNames(typography.subSectionHeader, typography.link)}
                href="/nP_Patient-Billing-Guide.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                Billing Information
              </a>
              {requireBillTypeSelection && (
                <div className={classes.inputContainer}>
                  <FormSingleSelectDropdown
                    name="billingType"
                    label={findFieldConfig('billingType', allNeuroConsentV2Fields).label}
                    validate={required}
                    placeholder={'Select Billing Type'}
                    options={[
                      { label: 'Self-Pay', value: BillingType.SELF_PAY },
                      { label: 'Government Insurance', value: BillingType.PUBLIC_INSURANCE },
                      { label: 'Commercial Insurance', value: BillingType.COMMERCIAL_INSURANCE },
                    ]}
                    onChangeSideEffects={resetBillingFields}
                    data-testid="billing-type-dropdown"
                  />
                </div>
              )}
              <div className={classes.contentContainer}>
                <NeuroConsentV2ContentPart2
                  faStatus={faStatus}
                  billingType={billingType}
                  useSelfPayFlow={useSelfPayFlow}
                  onClickFaLink={() => {
                    history.push('/onboarding/consent-to-test/financial-assistance');
                  }}
                />
              </div>
              {billingType === BillingType.SELF_PAY && (
                <SelfPayPaymentPlanSubform
                  faStatus={faStatus}
                  stripeCheckoutToken={stripeCheckoutToken}
                  setStripeComplete={setIsStripeComplete}
                />
              )}
              {billingType === BillingType.COMMERCIAL_INSURANCE && (
                <>
                  <InsuranceInformationSubform
                    insuranceType={InsuranceType.Private}
                    onCompanyNameChange={resetInsuranceFields}
                  />
                  <CommercialInsurancePaymentPlanSubform
                    faStatus={faStatus}
                    stripeCheckoutToken={stripeCheckoutToken}
                    setStripeComplete={setIsStripeComplete}
                    forceUseSelfPayFlow={insuranceCompanyName === OTHER_OPTION.value}
                  />
                </>
              )}
              {billingType === BillingType.PUBLIC_INSURANCE && collectGovernmentInsurance && (
                <InsuranceInformationSubform
                  insuranceType={InsuranceType.Public}
                  onCompanyNameChange={resetInsuranceFields}
                />
              )}
              <div className={classes.contentContainer}>
                <NeuroConsentV2ContentPart3 />
              </div>
              <div className={classes.contentContainer}>
                <NeuroConsentV2ContentPart4 />
              </div>
              <div className={classes.inputContainer}>
                <FormSingleSelectDropdown
                  label={findFieldConfig('isLegallyAuthorizedRepresentative', allNeuroConsentV2Fields).label}
                  name="isLegallyAuthorizedRepresentative"
                  validate={required}
                  options={IS_LEGALLY_AUTHORIZED_REPRESENTATIVE_OPTIONS_V2}
                  data-testid="is-legally-authorized-representative"
                />
                <Condition when="isLegallyAuthorizedRepresentative" is={IsLegallyAuthorizedRepresentativeOption.YES}>
                  <FormTextInput
                    label={findFieldConfig('legallyAuthorizedRepresentativeFirstName', allNeuroConsentV2Fields).label}
                    placeholder="Representative first name"
                    name="legallyAuthorizedRepresentativeFirstName"
                    validate={required}
                    data-testid="representative-first-name"
                  />
                  <FormTextInput
                    label={findFieldConfig('legallyAuthorizedRepresentativeLastName', allNeuroConsentV2Fields).label}
                    placeholder="Representative last name"
                    name="legallyAuthorizedRepresentativeLastName"
                    validate={required}
                    data-testid="representative-last-name"
                  />
                  <FormTextInput
                    label={
                      findFieldConfig('legallyAuthorizedRepresentativeRelationshipToPatient', allNeuroConsentV2Fields)
                        .label
                    }
                    placeholder="Signee Relationship"
                    name="legallyAuthorizedRepresentativeRelationshipToPatient"
                    validate={required}
                    data-testid="signee-relationship"
                  />
                  <FormSignature
                    label={findFieldConfig('representativeSignature', allNeuroConsentV2Fields).label}
                    name={'representativeSignature'}
                    validate={required}
                    data-testid="representative-signature-with-date"
                  />
                </Condition>
                <Condition when="isLegallyAuthorizedRepresentative" is={IsLegallyAuthorizedRepresentativeOption.NO}>
                  <FormSignature
                    label={findFieldConfig('signature', allNeuroConsentV2Fields).label}
                    name={'signature'}
                    validate={required}
                    data-testid="patient-signature-with-date"
                  />
                </Condition>
              </div>
              <Grid
                item
                container
                flexGrow={1}
                padding="30px 0px"
                gap="15px"
                justifyContent="center"
                alignItems="flex-end"
                position="sticky"
                bottom={0}
                bgcolor="#fff"
              >
                <Button
                  ariaLabel={submitText}
                  onClick={handleSubmit}
                  loading={submitting}
                  disabled={!valid || (!bypassStripeRequirement && !isStripeComplete) || submitting}
                  data-testid="continue-button"
                  classes={{
                    root: classes.button,
                  }}
                >
                  <Typography variant="button">{submitText}</Typography>
                </Button>
              </Grid>
            </>
          );
        }}
      />
    </Grid>
  );
};

const ConsentToTestWrapper: React.FC<{ nextAction?: () => Promise<void> }> = ({ nextAction }) => {
  const FinancialAssistanceComponent = featureEnabledInEnvironment('enableNeuroFinancialAssistanceV2')
    ? FinancialAssistanceV2
    : FinancialAssistanceV1;
  return (
    <Switch>
      <Route path="/onboarding/consent-to-test/financial-assistance" component={FinancialAssistanceComponent} />
      <Route render={() => <ConsentToTest nextAction={nextAction} />} />
    </Switch>
  );
};

export default withPortalContainer(ConsentToTestWrapper);

export const OnboardingConsent: React.FC<{ next?: () => Promise<void> }> = ({ next }) => (
  <ConsentToTestWrapper nextAction={next} />
);
